import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/api";
import { AGE_STYLES, formatFee } from "@/lib/candidate-utils";
import { Plus, X, Bell, Clock, FileText, Warning, UsersThree, Trash, MagnifyingGlass, Link as LinkIcon, Fire } from "@phosphor-icons/react";
import { NSW_REGIONS, regionsForState } from "@/lib/regions";

const STATUS_TABS = [
  { key: "active", label: "Active" },
  { key: "booked_out", label: "Booked Out" },
  { key: "placed", label: "Placed" },
  { key: "countered", label: "Countered" },
  { key: "lost", label: "Lost" },
];

const STATE_TABS = [
  { key: "all", label: "All", short: "All" },
  { key: "NSW", label: "NSW", short: "NSW" },
  { key: "QLD", label: "QLD", short: "QLD" },
  { key: "other", label: "Other", short: "Other" },
];

const STATE_PILL = {
  NSW: "border-sky-500 bg-sky-50 text-sky-700",
  QLD: "border-amber-500 bg-amber-50 text-amber-700",
  other: "border-zinc-300 bg-zinc-50 text-zinc-600",
};

export default function Candidates() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get("status");
  const validInitial = STATUS_TABS.some((t) => t.key === initialStatus)
    ? initialStatus
    : "active";
  const [candidates, setCandidates] = useState([]);
  const [error, setError] = useState("");
  const [status, setStatus] = useState(validInitial);
  const [stateFilter, setStateFilter] = useState(() => {
    const s = searchParams.get("state");
    return STATE_TABS.some((t) => t.key === s) ? s : "all";
  });
  const [regionFilter, setRegionFilter] = useState(() => searchParams.get("region") || "");
  const [ageFilter, setAgeFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status };
      if (stateFilter !== "all") params.state = stateFilter;
      if (stateFilter === "NSW" && regionFilter) params.region = regionFilter;
      const { data } = await api.get("/candidates", { params });
      setCandidates(data);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [status, stateFilter, regionFilter]);

  useEffect(() => {
    load();
    const qs = {};
    if (status !== "active") qs.status = status;
    if (stateFilter !== "all") qs.state = stateFilter;
    if (stateFilter === "NSW" && regionFilter) qs.region = regionFilter;
    setSearchParams(qs, { replace: true });
  }, [status, stateFilter, regionFilter, load, setSearchParams]);

  // If user switches away from NSW, clear the region filter automatically
  useEffect(() => {
    if (stateFilter !== "NSW" && regionFilter) setRegionFilter("");
  }, [stateFilter, regionFilter]);

  const filtered = candidates
    .filter((c) => (ageFilter === "all" ? true : c.age_category === ageFilter))
    .filter((c) => !search.trim() || c.name.toLowerCase().includes(search.toLowerCase().trim()));

  const currentTab = STATUS_TABS.find((t) => t.key === status);
  const currentLabel = currentTab?.label || status;
  const isBookedOut = status === "booked_out";

  return (
    <div className="space-y-8" data-testid="candidates-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
            {status === "active" ? "Candidates on books" : `${currentLabel} folder`}
          </div>
          <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl leading-none">
            {status === "active" ? "Candidates" : currentLabel}
          </h1>
          <div className="text-sm text-zinc-500 mt-3">
            {status === "active"
              ? `${candidates.length} on books · colour codes are days-on-books (green ≤3, yellow 4–7, red 8+)`
              : isBookedOut
              ? `${candidates.length} candidate${candidates.length === 1 ? "" : "s"} · all 3 interviews confirmed · awaiting outcomes`
              : `${candidates.length} closed ${status} · fees: ${formatFee(
                  candidates.reduce((s, c) => s + (Number(c.fee_approx) || 0), 0)
                )}`}
          </div>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="new-candidate-btn"
          className="flex items-center gap-2 bg-zinc-950 text-white px-5 py-3 rounded-sm hover:bg-zinc-800 transition-colors text-sm font-medium"
        >
          <Plus size={16} weight="bold" /> New candidate
        </button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            data-testid="candidates-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search candidates by name…"
            className="w-full bg-white border border-zinc-200 pl-9 pr-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
          />
        </div>
        {search && (
          <button
            onClick={() => setSearch("")}
            className="text-xs text-zinc-500 hover:text-zinc-950"
            data-testid="clear-search"
          >
            Clear
          </button>
        )}
        <div className="flex items-center gap-1 ml-auto">
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mr-2">State</span>
          {STATE_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setStateFilter(t.key)}
              data-testid={`state-tab-${t.key}`}
              className={`text-xs uppercase tracking-[0.15em] px-2.5 py-1.5 border rounded-sm transition-colors ${
                stateFilter === t.key
                  ? "border-zinc-950 bg-zinc-950 text-white"
                  : "border-zinc-200 text-zinc-600 hover:border-zinc-400"
              }`}
            >
              {t.short}
            </button>
          ))}
        </div>
      </div>

      {stateFilter === "NSW" && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="nsw-region-filter">
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">NSW region</span>
          <select
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            data-testid="region-filter-select"
            className="bg-white border border-zinc-200 px-3 py-1.5 text-xs rounded-sm focus:outline-none focus:border-zinc-950"
          >
            <option value="">All regions</option>
            {NSW_REGIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          {regionFilter && (
            <button
              onClick={() => setRegionFilter("")}
              className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-950"
              data-testid="clear-region-filter"
            >
              Clear
            </button>
          )}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-4 border-b border-zinc-200">
        <div className="flex items-center gap-1">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setStatus(t.key)}
              data-testid={`status-tab-${t.key}`}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                status === t.key
                  ? "border-zinc-950 text-zinc-950"
                  : "border-transparent text-zinc-500 hover:text-zinc-950"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {status === "active" && (
          <div className="flex items-center gap-2 pb-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mr-1">Age</span>
            {["all", "green", "yellow", "red"].map((k) => (
              <button
                key={k}
                onClick={() => setAgeFilter(k)}
                data-testid={`age-filter-${k}`}
                className={`text-xs uppercase tracking-[0.15em] px-2.5 py-1 border rounded-sm transition-colors ${
                  ageFilter === k
                    ? "border-zinc-950 bg-zinc-950 text-white"
                    : "border-zinc-200 text-zinc-600 hover:border-zinc-400"
                }`}
              >
                {k === "all" ? "All" : (
                  <span className="flex items-center gap-1.5">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${AGE_STYLES[k].dot}`} />
                    {k}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] p-4 rounded-sm text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm uppercase tracking-[0.2em]">Loading</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-sm p-16 text-center" data-testid="empty-state">
          <div className="font-heading font-bold text-2xl tracking-tight mb-2">
            {isBookedOut
              ? "No booked-out candidates"
              : candidates.length === 0 ? "No candidates yet" : "No matches"}
          </div>
          <div className="text-sm text-zinc-500 mb-8 max-w-md mx-auto">
            {isBookedOut
              ? "Candidates land here automatically once all 3 interviews are scheduled and invites have been sent to both parties."
              : candidates.length === 0
              ? "Register your first candidate. You get 3 interview slots to fill, then a top-up if you need more."
              : "Adjust the filters above."}
          </div>
          {!isBookedOut && candidates.length === 0 && (
            <button
              onClick={() => setShowCreate(true)}
              className="bg-zinc-950 text-white px-6 py-3 rounded-sm hover:bg-zinc-800 transition-colors text-sm font-medium"
              data-testid="empty-create-btn"
            >
              Register candidate
            </button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="candidate-grid">
          {filtered.map((c) => (
            <CandidateCard key={c.id} c={c} onClick={() => navigate(`/candidates/${c.id}`)} />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateCandidateModal
          onClose={() => setShowCreate(false)}
          onCreated={(c) => {
            setShowCreate(false);
            navigate(`/candidates/${c.id}`);
          }}
        />
      )}
    </div>
  );
}

function CandidateCard({ c, onClick }) {
  const styles = AGE_STYLES[c.age_category];
  const needsBooking = c.status === "active" && c.booked_count < 3;
  const recruiters = c.recruiters || [];
  return (
    <button
      onClick={onClick}
      data-testid={`candidate-card-${c.id}`}
      className={`text-left bg-white border border-zinc-200 rounded-sm p-5 hover:border-zinc-950 transition-colors ${styles.ring}`}
    >
      <div className="flex items-start justify-between gap-2 mb-4">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
            {c.role_target || "No target role"}
          </div>
          <div className="font-heading font-bold text-lg tracking-tight leading-tight truncate">
            {c.name}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <div className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${styles.pill}`}>
            {c.days_on_books}d
          </div>
          {c.state && (
            <div
              data-testid={`state-pill-${c.id}`}
              className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border rounded-sm ${
                STATE_PILL[c.state] || STATE_PILL.other
              }`}
            >
              {c.state}
            </div>
          )}
          {c.state === "NSW" && c.region && (
            <div
              data-testid={`region-pill-${c.id}`}
              className="text-[10px] tracking-[0.05em] px-2 py-0.5 border border-sky-300 bg-sky-50 text-sky-800 rounded-sm truncate max-w-[160px]"
              title={c.region}
            >
              {c.region}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-1 text-xs text-zinc-600">
          <Clock size={12} weight="bold" />
          <span className={needsBooking ? "font-semibold text-zinc-950" : ""}>
            {c.booked_count}/3 booked
          </span>
        </div>
        {c.cv_attachment_id && (
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-emerald-700 bg-emerald-50 border border-emerald-500 px-1.5 py-0.5">
            <FileText size={10} weight="bold" /> CV
          </div>
        )}
        {recruiters.length === 2 && (
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-zinc-700 bg-zinc-100 border border-zinc-300 px-1.5 py-0.5">
            <UsersThree size={10} weight="bold" /> Split
          </div>
        )}
        {c.linked_deal_count > 1 && (
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-emerald-800 bg-emerald-100 border border-emerald-300 px-1.5 py-0.5" data-testid={`linked-badge-${c.id}`}>
            <LinkIcon size={10} weight="bold" /> {c.linked_deal_count} deals
          </div>
        )}
        {c.booked_out && (
          <div
            data-testid={`booked-out-badge-${c.id}`}
            className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-violet-900 bg-violet-100 border border-violet-400 px-1.5 py-0.5"
            title="All 3 interviews confirmed with invites sent"
          >
            <Clock size={10} weight="bold" /> Booked Out
          </div>
        )}
      </div>

      {needsBooking && (
        <div className="flex items-center gap-1.5 text-xs text-[#B91C1C] mb-4">
          <Warning size={12} weight="bold" />
          Needs {3 - c.booked_count} more interview{3 - c.booked_count === 1 ? "" : "s"}
        </div>
      )}

      {c.urgency_reason && c.status === "active" && (
        <div
          data-testid={`urgency-chip-${c.id}`}
          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-2 py-1 mb-3 rounded-sm border ${
            c.age_category === "red"
              ? "border-[#FF2A2A] bg-red-50 text-[#B91C1C]"
              : c.age_category === "yellow"
              ? "border-amber-500 bg-amber-50 text-amber-800"
              : "border-zinc-200 bg-zinc-50 text-zinc-600"
          }`}
          title={`Urgency score: ${c.urgency_score}`}
        >
          <Fire size={10} weight="bold" />
          {c.urgency_reason}
        </div>
      )}

      <div className="flex items-end justify-between border-t border-zinc-200 pt-3 mt-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Fee</div>
          <div className="font-heading font-bold text-lg">{formatFee(c.fee_approx)}</div>
          {recruiters.length > 1 && c.credited_fee > 0 && c.credited_fee < c.fee_approx && (
            <div
              data-testid={`credited-fee-${c.id}`}
              className="text-[10px] text-zinc-500 mt-0.5"
              title="Your share after the split"
            >
              Yours · {formatFee(c.credited_fee)}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            {recruiters.length > 1 ? "Recruiters" : "Recruiter"}
          </div>
          <div className="text-xs font-medium truncate max-w-[180px]">
            {recruiters.length === 0
              ? "Unassigned"
              : recruiters.length === 1
              ? recruiters[0].name
              : `${recruiters[0].name} · ${recruiters[1].name}`}
          </div>
        </div>
      </div>
    </button>
  );
}

function CreateCandidateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", role_target: "", fee_approx: "", candidate_email: "", candidate_phone: "", state: "", region: "" });
  const [team, setTeam] = useState([]);
  const [recruiter1, setRecruiter1] = useState("");
  const [recruiter2, setRecruiter2] = useState("");
  const [split, setSplit] = useState(50); // % for recruiter1 when both set
  const [error, setError] = useState("");
  const [dupWarning, setDupWarning] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch((err) => console.error("Failed to load team:", err));
  }, []);

  const buildRecruiters = () => {
    const list = [];
    if (recruiter1) list.push({ user_id: recruiter1, split_pct: recruiter2 ? Number(split) : 100 });
    if (recruiter2 && recruiter2 !== recruiter1) {
      list.push({ user_id: recruiter2, split_pct: Number(100 - split) });
    }
    return list;
  };

  const submit = async (e, force = false, linkToId = null) => {
    if (e) e.preventDefault();
    setSubmitting(true);
    setError("");
    setDupWarning(null);
    try {
      const body = {
        name: form.name,
        role_target: form.role_target,
        fee_approx: Number(form.fee_approx || 0),
        candidate_email: form.candidate_email || undefined,
        candidate_phone: form.candidate_phone || "",
        state: form.state || "",
        region: form.state === "NSW" ? (form.region || "") : "",
        recruiters: buildRecruiters(),
        force: force || !!linkToId,
      };
      if (form.candidate_email) body.candidate_email = form.candidate_email;
      if (linkToId) body.link_to_candidate_id = linkToId;
      const { data } = await api.post("/candidates", body);
      onCreated(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && typeof detail === "object") {
        setDupWarning(detail);
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const both = recruiter1 && recruiter2 && recruiter2 !== recruiter1;

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/50 flex items-center justify-center p-4" data-testid="create-candidate-modal">
      <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Register</div>
            <div className="font-heading font-bold text-2xl tracking-tight">New candidate</div>
          </div>
          <button onClick={onClose} data-testid="close-create-btn" className="text-zinc-500 hover:text-zinc-950">
            <X size={20} weight="bold" />
          </button>
        </div>
        <form onSubmit={(e) => submit(e, false)} className="p-6 space-y-4">
          <Field label="Candidate name" required>
            <input
              data-testid="candidate-name-input"
              required
              value={form.name}
              onChange={(e) => { set("name", e.target.value); setDupWarning(null); }}
              className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
              placeholder="Jane Doe"
              autoFocus
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Target role">
              <input
                data-testid="candidate-role-input"
                value={form.role_target}
                onChange={(e) => set("role_target", e.target.value)}
                className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
                placeholder="Senior Nurse"
              />
            </Field>
            <Field label="Fee (A$)">
              <input
                data-testid="candidate-fee-input"
                type="number"
                min="0"
                step="100"
                value={form.fee_approx}
                onChange={(e) => set("fee_approx", e.target.value)}
                className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
                placeholder="10000"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Candidate email">
              <input
                data-testid="candidate-email-input"
                type="email"
                value={form.candidate_email}
                onChange={(e) => { set("candidate_email", e.target.value); setDupWarning(null); }}
                className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
                placeholder="jane@example.com"
              />
            </Field>
            <Field label="Candidate phone">
              <input
                data-testid="candidate-phone-input"
                type="tel"
                value={form.candidate_phone}
                onChange={(e) => set("candidate_phone", e.target.value)}
                className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
                placeholder="+61 400 000 000"
              />
            </Field>
          </div>

          <Field label="State">
            <div className="flex gap-2" data-testid="candidate-state-picker">
              {[
                { v: "NSW", label: "NSW" },
                { v: "QLD", label: "QLD" },
                { v: "other", label: "Other" },
                { v: "", label: "Unassigned" },
              ].map((s) => (
                <button
                  key={s.v || "unset"}
                  type="button"
                  onClick={() => set("state", s.v)}
                  data-testid={`state-option-${s.v || "unset"}`}
                  className={`flex-1 border rounded-sm px-3 py-2 text-xs font-medium uppercase tracking-[0.15em] transition-colors ${
                    form.state === s.v
                      ? "border-zinc-950 bg-zinc-950 text-white"
                      : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </Field>

          {form.state === "NSW" && (
            <Field label="NSW region">
              <select
                value={form.region}
                onChange={(e) => set("region", e.target.value)}
                data-testid="candidate-region-select"
                className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
              >
                <option value="">— Pick a region —</option>
                {NSW_REGIONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </Field>
          )}

          <div className="border border-zinc-200 rounded-sm p-3 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
              <UsersThree size={12} weight="bold" /> Recruiter(s) & fee split
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Recruiter 1</label>
                <select
                  data-testid="recruiter-1-select"
                  value={recruiter1}
                  onChange={(e) => setRecruiter1(e.target.value)}
                  className="w-full bg-white border border-zinc-200 px-2 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
                >
                  <option value="">— Assign to me —</option>
                  {team.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Recruiter 2 (optional)</label>
                <select
                  data-testid="recruiter-2-select"
                  value={recruiter2}
                  onChange={(e) => setRecruiter2(e.target.value)}
                  className="w-full bg-white border border-zinc-200 px-2 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
                >
                  <option value="">— None —</option>
                  {team
                    .filter((u) => u.id !== recruiter1)
                    .map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                </select>
              </div>
            </div>
            {both && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    Fee split
                  </label>
                  <div className="text-xs font-medium">
                    R1: {split}% · R2: {100 - split}%
                  </div>
                </div>
                <input
                  data-testid="split-slider"
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={split}
                  onChange={(e) => setSplit(Number(e.target.value))}
                  className="w-full accent-zinc-950"
                />
                <div className="text-[10px] text-zinc-500 mt-1">
                  On placement, each recruiter's share counts toward their monthly target.
                </div>
              </div>
            )}
          </div>

          {dupWarning && (
            <div className="border border-amber-500 bg-amber-50 text-amber-800 px-3 py-3 rounded-sm text-sm" data-testid="dup-warning">
              <div className="font-medium mb-1 flex items-center gap-1.5">
                <Warning size={14} weight="bold" />
                {dupWarning.message || "Duplicate found"}
              </div>
              <div className="text-xs mb-3">
                Existing: <b>{dupWarning.existing_name}</b>
                {dupWarning.existing_email && <> · {dupWarning.existing_email}</>}
                {" · "}status: {dupWarning.existing_status}
              </div>
              <div className="text-[11px] text-amber-900/70 mb-3 leading-relaxed">
                <b>Link to existing profile</b> keeps this new deal separate on your books but stitches it to the previous record — so you'll see both under one banner and carry over history. Recommended when the same person is interviewing again.
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => submit(null, false, dupWarning.existing_id)}
                  data-testid="link-existing-btn"
                  className="bg-emerald-700 text-white px-3 py-1.5 rounded-sm text-xs font-medium hover:bg-emerald-800"
                >
                  Link to existing profile
                </button>
                <button
                  type="button"
                  onClick={() => submit(null, true)}
                  data-testid="force-create-btn"
                  className="bg-zinc-950 text-white px-3 py-1.5 rounded-sm text-xs font-medium hover:bg-zinc-800"
                >
                  Register anyway
                </button>
                <button
                  type="button"
                  onClick={() => setDupWarning(null)}
                  className="border border-zinc-200 bg-white text-zinc-950 px-3 py-1.5 rounded-sm text-xs font-medium hover:bg-zinc-100"
                >
                  {dupWarning.matched_by === "email" ? "Change email" : "Change name"}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] text-sm px-3 py-2 rounded-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-zinc-200 bg-white text-zinc-950 py-3 rounded-sm font-medium hover:bg-zinc-100 transition-colors text-sm" data-testid="cancel-create-btn">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !form.name || !!dupWarning}
              data-testid="submit-create-candidate-btn"
              className="flex-1 bg-zinc-950 text-white py-3 rounded-sm font-medium hover:bg-zinc-800 transition-colors text-sm disabled:opacity-50"
            >
              {submitting ? "Creating..." : "Register candidate"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, children, required }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">
      {label}{required && <span className="text-[#FF2A2A]"> *</span>}
    </label>
    {children}
  </div>
);
